import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, doc, getDoc, setDoc, updateDoc, orderBy, where, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../../firebase/config';
import {
  Building2, Search, Plus, ArrowLeft, Save, Trash2, Eye, Edit3,
  Star, Briefcase, Receipt, Phone, Image as ImageIcon, X,
  CheckCircle, AlertCircle, Users, ToggleLeft, ToggleRight
} from 'lucide-react';

const SPECIALTY_PRESETS = [
  '크라운/브릿지', '임플란트 보철', '교정장치', '의치 제작/수리',
  'CAD/CAM 디지털 기공', '라미네이트/비니어', '지르코니아',
  '올세라믹', '투명교정', '스플린트', '임시 보철'
];
const SCANNER_PRESETS = ['Medit', '3Shape TRIOS', 'Primescan', 'iTero', 'Shining 3D', 'CEREC', '기타'];
const REGION_PRESETS = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '강원', '충청', '전라', '경상', '제주', '전국 택배'];
const PRICE_TABLE_PRESETS = ['지르코니아 크라운', 'PFM 크라운', 'E-max 크라운', '임플란트 크라운', '커스텀 어버트먼트', '인레이/온레이', '라미네이트', 'PMMA 임시치', '총의치', '교정장치'];

const SECTION_CONFIG = [
  { num: '01', title: '기본 정보', icon: Star },
  { num: '02', title: '전문 분야', icon: Briefcase },
  { num: '03', title: '단가표', icon: Receipt },
  { num: '04', title: '포트폴리오', icon: ImageIcon },
  { num: '05', title: '연락처', icon: Phone },
];

const DEFAULT_FORM = {
  slogan: '', representative: '', foundingYear: '',
  introduction: '', detailedIntro: '',
  specialties: [], priceTable: [],
  contactPhone: '', contactEmail: '', contactWebsite: '', workingHours: '',
  compatibleScanners: [], deliveryRegions: [],
  firstDealBenefit: '', remakeRate: '', monthlyCapacity: '',
};

function AdminLabAdvertising() {
  // List view state
  const [labs, setLabs] = useState([]);
  const [labUsers, setLabUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Edit view state
  const [editMode, setEditMode] = useState(false);
  const [editLabId, setEditLabId] = useState(null);
  const [editLabName, setEditLabName] = useState('');
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [adData, setAdData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Image state
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [portfolioPreviews, setPortfolioPreviews] = useState([]);
  const [studioImages, setStudioImages] = useState([]);
  const [studioPreviews, setStudioPreviews] = useState([]);

  // Lab user picker for new ad
  const [showLabPicker, setShowLabPicker] = useState(false);
  const [labPickerSearch, setLabPickerSearch] = useState('');

  // Detail modal
  const [selectedLab, setSelectedLab] = useState(null);

  useEffect(() => { loadLabs(); }, []);
  useEffect(() => { filterLabs(); }, [labs, search, filterStatus]);

  const loadLabs = async () => {
    try {
      setLoading(true);
      // Load existing lab advertisements
      const q = query(collection(db, 'labAdvertisements'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setLabs(snap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load lab users (for creating new ads)
      const uq = query(collection(db, 'users'), where('userType', '==', 'lab'));
      const usnap = await getDocs(uq);
      setLabUsers(usnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const filterLabs = () => {
    let f = labs;
    if (filterStatus === 'active') f = f.filter(l => l.isActive);
    else if (filterStatus === 'inactive') f = f.filter(l => !l.isActive);
    if (search.trim()) {
      const q = search.toLowerCase();
      f = f.filter(l => (l.businessName || '').toLowerCase().includes(q) || (l.address || '').toLowerCase().includes(q) || (l.slogan || '').toLowerCase().includes(q));
    }
    setFiltered(f);
  };

  const showSuccess = (msg) => { setSuccessMsg(msg); setErrorMsg(''); setTimeout(() => setSuccessMsg(''), 3000); };
  const showError = (msg) => { setErrorMsg(msg); setSuccessMsg(''); setTimeout(() => setErrorMsg(''), 5000); };
  const updateForm = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const toggleTag = (field, value) => {
    setFormData(prev => {
      const arr = prev[field];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };
  const updatePriceRow = (index, key, value) => {
    setFormData(prev => {
      const table = [...prev.priceTable];
      table[index] = { ...table[index], [key]: value };
      return { ...prev, priceTable: table };
    });
  };

  // Start editing existing lab ad
  const startEdit = async (labId, labName) => {
    try {
      setEditLabId(labId);
      setEditLabName(labName || '기공소');
      setEditMode(true);

      const adRef = doc(db, 'labAdvertisements', labId);
      const adSnap = await getDoc(adRef);

      if (adSnap.exists()) {
        const data = adSnap.data();
        setAdData(data);
        setFormData({
          slogan: data.slogan || '',
          representative: data.representative || '',
          foundingYear: data.foundingYear || '',
          introduction: data.introduction || '',
          detailedIntro: data.detailedIntro || data.philosophy || '',
          specialties: Array.isArray(data.specialties) ? data.specialties :
            (Array.isArray(data.services) ? data.services.map(s => typeof s === 'string' ? s : s.name) : []),
          priceTable: Array.isArray(data.priceTable) ? data.priceTable : [],
          contactPhone: data.contactPhone || '',
          contactEmail: data.contactEmail || '',
          contactWebsite: data.contactWebsite || '',
          workingHours: data.workingHours || '',
          compatibleScanners: Array.isArray(data.compatibleScanners) ? data.compatibleScanners : [],
          deliveryRegions: Array.isArray(data.deliveryRegions) ? data.deliveryRegions : [],
          firstDealBenefit: data.firstDealBenefit || '',
          remakeRate: data.remakeRate || '',
          monthlyCapacity: data.monthlyCapacity || '',
        });
        setProfilePreview(data.profileImageUrl || null);
        setPortfolioPreviews(data.portfolioImages || []);
        setStudioPreviews(data.studioImages || []);
      } else {
        setAdData(null);
        setFormData({ ...DEFAULT_FORM });
        setProfilePreview(null);
        setPortfolioPreviews([]);
        setStudioPreviews([]);
      }

      setProfileImage(null);
      setPortfolioImages([]);
      setStudioImages([]);
    } catch (e) {
      console.error(e);
      showError('데이터 로드 실패');
    }
  };

  // Start creating new ad for a lab user
  const startNewAd = async (labUser) => {
    setShowLabPicker(false);
    await startEdit(labUser.id, labUser.businessName || labUser.companyName || labUser.email);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditLabId(null);
    setAdData(null);
    setFormData({ ...DEFAULT_FORM });
    setProfileImage(null);
    setProfilePreview(null);
    setPortfolioImages([]);
    setPortfolioPreviews([]);
    setStudioImages([]);
    setStudioPreviews([]);
  };

  // Image handlers
  const handleImageUpload = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showError('이미지는 5MB 이하로 업로드해주세요.'); return; }
    setFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handlePortfolioImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + portfolioPreviews.length > 20) { showError('포트폴리오 이미지는 최대 20개까지'); return; }
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
    setPortfolioImages(prev => [...prev, ...valid]);
    setPortfolioPreviews(prev => [...prev, ...valid.map(f => URL.createObjectURL(f))]);
  };

  const removePortfolioImage = (index) => {
    setPortfolioImages(prev => prev.filter((_, i) => i !== index));
    setPortfolioPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleStudioImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + studioPreviews.length > 10) { showError('내부사진은 최대 10개까지'); return; }
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
    setStudioImages(prev => [...prev, ...valid]);
    setStudioPreviews(prev => [...prev, ...valid.map(f => URL.createObjectURL(f))]);
  };

  const removeStudioImage = (index) => {
    setStudioImages(prev => prev.filter((_, i) => i !== index));
    setStudioPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const extractRegion = (address) => {
    if (!address) return '';
    const regions = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];
    for (const r of regions) { if (address.includes(r)) return r; }
    return address.split(' ')[0] || '';
  };

  // Save
  const handleSave = async () => {
    if (!editLabId) return;
    if (formData.specialties.length === 0) { showError('최소 1개 이상의 전문 분야를 선택해주세요.'); return; }
    try {
      setSaving(true);
      const userDoc = await getDoc(doc(db, 'users', editLabId));
      const userData = userDoc.exists() ? userDoc.data() : {};

      let profileImageUrl = adData?.profileImageUrl || null;
      let portfolioUrls = portfolioPreviews.filter(u => !u.startsWith('blob:'));
      let studioUrls = studioPreviews.filter(u => !u.startsWith('blob:'));

      if (profileImage) {
        const profileRef = ref(storage, `labAds/${editLabId}/profile_${Date.now()}.jpg`);
        await uploadBytes(profileRef, profileImage);
        profileImageUrl = await getDownloadURL(profileRef);
      }

      if (portfolioImages.length > 0) {
        const uploads = portfolioImages.map(async (file, i) => {
          const r = ref(storage, `labAds/${editLabId}/portfolio_${Date.now()}_${i}.jpg`);
          await uploadBytes(r, file);
          return await getDownloadURL(r);
        });
        portfolioUrls = [...portfolioUrls, ...(await Promise.all(uploads))];
      }

      if (studioImages.length > 0) {
        const uploads = studioImages.map(async (file, i) => {
          const r = ref(storage, `labAds/${editLabId}/studio_${Date.now()}_${i}.jpg`);
          await uploadBytes(r, file);
          return await getDownloadURL(r);
        });
        studioUrls = [...studioUrls, ...(await Promise.all(uploads))];
      }

      const adRef = doc(db, 'labAdvertisements', editLabId);
      const adDataToSave = {
        ...formData,
        profileImageUrl,
        heroImageUrl: profileImageUrl || '',
        portfolioImages: portfolioUrls,
        studioImages: studioUrls,
        specialties: formData.specialties,
        introduction: formData.introduction || '',
        philosophy: formData.detailedIntro || '',
        services: formData.specialties.map(s => ({ name: s, description: '', keywords: '' })),
        // 저장은 활성/비활성 상태를 변경하지 않음 — 활성/비활성은 별도 버튼으로만 토글
        // 신규 생성 시에는 비활성으로 시작 (기공소가 직접 또는 관리자가 명시적으로 활성 버튼 클릭 필요)
        isActive: adData ? (adData.isActive === true) : false,
        status: adData ? (adData.status || (adData.isActive === true ? 'active' : 'inactive')) : 'inactive',
        labId: editLabId,
        businessName: userData?.businessName || userData?.companyName || '',
        email: userData?.email || '',
        phone: userData?.phone || '',
        address: userData?.address || '',
        updatedAt: new Date(),
        createdAt: adData?.createdAt || new Date(),
        lastEditedBy: 'admin',
      };

      if (adData) { await updateDoc(adRef, adDataToSave); }
      else { await setDoc(adRef, adDataToSave); }

      // Sync matchingLabs
      const matchingRef = doc(db, 'matchingLabs', editLabId);
      const matchSnap = await getDoc(matchingRef);
      if (matchSnap.exists()) {
        await updateDoc(matchingRef, {
          labName: userData?.businessName || userData?.companyName || '',
          email: userData?.email || '',
          phone: userData?.phone || '',
          region: extractRegion(userData?.address),
          detailAddress: userData?.address || '',
          specialties: formData.specialties,
          introduction: formData.introduction,
          updatedAt: serverTimestamp()
        });
      }

      // users.isPublic 동기화: 저장은 상태를 변경하지 않음 (활성/비활성 버튼으로만 토글)
      if (userDoc.exists()) {
        const nextIsPublic = adData ? (adData.isActive === true) : false;
        await updateDoc(doc(db, 'users', editLabId), { isPublic: nextIsPublic, updatedAt: new Date().toISOString() });
      }

      setProfileImage(null);
      setPortfolioImages([]);
      setStudioImages([]);
      showSuccess('홍보페이지가 저장되었습니다.');
      await loadLabs();
      // Reload current edit data
      await startEdit(editLabId, editLabName);
    } catch (error) {
      console.error('저장 실패:', error);
      showError('저장에 실패했습니다: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // 활성/비활성 토글
  const toggleActive = async (lab) => {
    const next = !lab.isActive;
    const action = next ? '활성화' : '비활성화';
    if (!window.confirm(`"${lab.businessName || '기공소'}" 홍보페이지를 ${action}하시겠습니까?\n\n${next ? '치과 회원이 검색·열람할 수 있게 됩니다.' : '치과 회원에게 더 이상 노출되지 않습니다.'}`)) return;
    try {
      const adRef = doc(db, 'labAdvertisements', lab.id);
      await updateDoc(adRef, {
        isActive: next,
        status: next ? 'active' : 'inactive',
        updatedAt: new Date(),
        deactivatedAt: next ? null : new Date(),
        lastEditedBy: 'admin',
      });
      // users.isPublic 동기화 (기공소 찾기 노출 여부)
      try {
        await updateDoc(doc(db, 'users', lab.id), {
          isPublic: next,
          updatedAt: new Date().toISOString(),
        });
      } catch (e) { /* user doc 없을 수 있음 */ }
      setLabs(prev => prev.map(l => l.id === lab.id ? { ...l, isActive: next, status: next ? 'active' : 'inactive' } : l));
      showSuccess(`${action} 완료`);
    } catch (e) {
      console.error('상태 변경 실패:', e);
      showError('상태 변경 실패: ' + e.message);
    }
  };

  // 편집 화면용: 현재 편집 중인 기공소 활성/비활성 변경
  const toggleEditActive = async (next) => {
    if (!editLabId) return;
    if (!adData) {
      showError('먼저 저장 버튼을 눌러 홍보페이지를 등록해주세요.');
      return;
    }
    if (next === (adData.isActive === true)) {
      showError(`이미 ${next ? '활성' : '비활성'} 상태입니다.`);
      return;
    }
    const action = next ? '활성화' : '비활성화';
    if (!window.confirm(`"${editLabName}" 홍보페이지를 ${action}하시겠습니까?\n\n${next ? '치과 회원이 검색·열람할 수 있게 됩니다.' : '치과 회원에게 더 이상 노출되지 않습니다.'}`)) return;
    try {
      setSaving(true);
      await updateDoc(doc(db, 'labAdvertisements', editLabId), {
        isActive: next,
        status: next ? 'active' : 'inactive',
        updatedAt: new Date(),
        deactivatedAt: next ? null : new Date(),
        lastEditedBy: 'admin',
      });
      try {
        await updateDoc(doc(db, 'users', editLabId), { isPublic: next, updatedAt: new Date().toISOString() });
      } catch (e) { /* skip */ }
      setAdData(prev => prev ? { ...prev, isActive: next, status: next ? 'active' : 'inactive' } : prev);
      setLabs(prev => prev.map(l => l.id === editLabId ? { ...l, isActive: next, status: next ? 'active' : 'inactive' } : l));
      showSuccess(`${action} 완료`);
    } catch (e) {
      console.error('상태 변경 실패:', e);
      showError('상태 변경 실패: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // 일괄 활성화/비활성화
  const bulkToggle = async (next) => {
    const targets = filtered.filter(l => l.isActive !== next);
    if (targets.length === 0) {
      showError(`이미 모두 ${next ? '활성' : '비활성'} 상태입니다.`);
      return;
    }
    if (!window.confirm(`현재 필터 결과 ${targets.length}개를 일괄 ${next ? '활성화' : '비활성화'} 하시겠습니까?`)) return;
    try {
      await Promise.all(targets.map(async (lab) => {
        await updateDoc(doc(db, 'labAdvertisements', lab.id), {
          isActive: next,
          status: next ? 'active' : 'inactive',
          updatedAt: new Date(),
          deactivatedAt: next ? null : new Date(),
          lastEditedBy: 'admin',
        });
        try {
          await updateDoc(doc(db, 'users', lab.id), { isPublic: next, updatedAt: new Date().toISOString() });
        } catch (e) { /* skip */ }
      }));
      setLabs(prev => prev.map(l => targets.find(t => t.id === l.id) ? { ...l, isActive: next, status: next ? 'active' : 'inactive' } : l));
      showSuccess(`${targets.length}개 ${next ? '활성화' : '비활성화'} 완료`);
    } catch (e) {
      console.error('일괄 변경 실패:', e);
      showError('일괄 변경 실패: ' + e.message);
    }
  };

  const fmtPrice = (p) => {
    const n = parseInt(p);
    if (isNaN(n)) return p;
    return n >= 10000 ? `${Math.floor(n / 10000)}만${n % 10000 >= 1000 ? Math.floor((n % 10000) / 1000) + '천' : ''}원` : `${n.toLocaleString()}원`;
  };

  const activeCount = labs.filter(l => l.isActive).length;

  // Labs that don't have an ad yet
  const labsWithoutAd = labUsers.filter(u => !labs.find(l => l.id === u.id));
  const filteredLabPicker = labsWithoutAd.filter(u => {
    if (!labPickerSearch.trim()) return true;
    const q = labPickerSearch.toLowerCase();
    return (u.businessName || '').toLowerCase().includes(q) || (u.companyName || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>로딩 중...</div>;

  // ===== EDIT MODE =====
  if (editMode) {
    return (
      <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <button onClick={cancelEdit} style={{ ...S.iconBtn, width: 36, height: 36 }}><ArrowLeft size={18} /></button>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>홍보페이지 편집</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#6366f1', fontWeight: 600 }}>
              {editLabName}
              {adData && (
                <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700,
                  backgroundColor: adData.isActive === true ? '#f0fdf4' : '#f1f5f9',
                  color: adData.isActive === true ? '#10b981' : '#94a3b8' }}>
                  {adData.isActive === true ? '활성' : '비활성'}
                </span>
              )}
            </p>
          </div>
          <div style={{ flex: 1 }} />
          {adData && (
            <>
              <button onClick={() => toggleEditActive(true)} disabled={saving || adData.isActive === true}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: adData.isActive === true ? '#f1f5f9' : '#10b981', color: adData.isActive === true ? '#94a3b8' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: adData.isActive === true ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                <ToggleRight size={16} /> 활성화
              </button>
              <button onClick={() => toggleEditActive(false)} disabled={saving || adData.isActive !== true}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: adData.isActive !== true ? '#f1f5f9' : '#ef4444', color: adData.isActive !== true ? '#94a3b8' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: adData.isActive !== true ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                <ToggleLeft size={16} /> 비활성화
              </button>
            </>
          )}
          <button onClick={handleSave} disabled={saving} style={{ ...S.saveBtn, opacity: saving ? 0.6 : 1 }} title="내용만 저장 (활성/비활성 상태는 변경되지 않습니다)">
            <Save size={16} />
            {saving ? '저장 중...' : '내용 저장'}
          </button>
        </div>

        <div style={{ padding: '10px 14px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 16, fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={14} />
          <span><b>안내:</b> "내용 저장"은 입력한 정보만 반영하고 활성/비활성 상태는 변경하지 않습니다. 노출 여부는 위 <b>활성화</b>/<b>비활성화</b> 버튼으로 제어합니다.</span>
        </div>

        {/* Messages */}
        {successMsg && <div style={S.successBanner}><CheckCircle size={16} /> {successMsg}</div>}
        {errorMsg && <div style={S.errorBanner}><AlertCircle size={16} /> {errorMsg}</div>}

        {/* 5 Sections */}
        {SECTION_CONFIG.map((sec, si) => {
          const Icon = sec.icon;
          return (
            <div key={si} style={S.section}>
              <div style={S.sectionHeader}>
                <div style={S.sectionNum}>{sec.num}</div>
                <Icon size={18} color="#6366f1" />
                <span style={{ fontSize: 15, fontWeight: 700 }}>{sec.title}</span>
              </div>

              {/* Section 01 - 기본 정보 */}
              {sec.num === '01' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Profile Image */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 100, height: 100, borderRadius: '50%', border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f8fafc', flexShrink: 0 }}>
                      {profilePreview ? <img src={profilePreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImageIcon size={24} color="#d1d5db" />}
                    </div>
                    <div>
                      <label style={S.uploadBtn}>
                        프로필 사진 업로드
                        <input type="file" accept="image/*" hidden onChange={e => handleImageUpload(e, setProfileImage, setProfilePreview)} />
                      </label>
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>권장: 정사각형, 5MB 이하</p>
                    </div>
                  </div>

                  <div style={S.inputGrid}>
                    <div>
                      <label style={S.inputLabel}>슬로건</label>
                      <input style={S.input} placeholder="예: 정밀한 디지털 기공" value={formData.slogan} onChange={e => updateForm('slogan', e.target.value)} />
                    </div>
                    <div>
                      <label style={S.inputLabel}>대표자</label>
                      <input style={S.input} value={formData.representative} onChange={e => updateForm('representative', e.target.value)} />
                    </div>
                    <div>
                      <label style={S.inputLabel}>설립연도</label>
                      <input style={S.input} type="number" placeholder="2020" value={formData.foundingYear} onChange={e => updateForm('foundingYear', e.target.value)} />
                    </div>
                  </div>

                  <div>
                    <label style={S.inputLabel}>한줄 소개</label>
                    <input style={S.input} placeholder="기공소를 한 줄로 소개해주세요" value={formData.introduction} onChange={e => updateForm('introduction', e.target.value)} />
                  </div>
                  <div>
                    <label style={S.inputLabel}>상세 소개</label>
                    <textarea style={{ ...S.input, minHeight: 100, resize: 'vertical' }} placeholder="기공소의 강점, 보유 장비, 작업 철학 등을 소개해주세요" value={formData.detailedIntro} onChange={e => updateForm('detailedIntro', e.target.value)} />
                  </div>
                </div>
              )}

              {/* Section 02 - 전문 분야 */}
              {sec.num === '02' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={S.inputLabel}>전문 분야 (1개 이상 선택)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {SPECIALTY_PRESETS.map(s => (
                        <button key={s} onClick={() => toggleTag('specialties', s)}
                          style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all .15s',
                            backgroundColor: formData.specialties.includes(s) ? '#eef2ff' : '#fff',
                            color: formData.specialties.includes(s) ? '#6366f1' : '#64748b',
                            borderColor: formData.specialties.includes(s) ? '#6366f1' : '#e2e8f0' }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={S.inputLabel}>호환 스캐너</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {SCANNER_PRESETS.map(s => (
                        <button key={s} onClick={() => toggleTag('compatibleScanners', s)}
                          style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            backgroundColor: formData.compatibleScanners.includes(s) ? '#f0fdf4' : '#fff',
                            color: formData.compatibleScanners.includes(s) ? '#16a34a' : '#64748b',
                            borderColor: formData.compatibleScanners.includes(s) ? '#16a34a' : '#e2e8f0' }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={S.inputLabel}>배송 지역</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {REGION_PRESETS.map(r => (
                        <button key={r} onClick={() => toggleTag('deliveryRegions', r)}
                          style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            backgroundColor: formData.deliveryRegions.includes(r) ? '#fff7ed' : '#fff',
                            color: formData.deliveryRegions.includes(r) ? '#c2410c' : '#64748b',
                            borderColor: formData.deliveryRegions.includes(r) ? '#c2410c' : '#e2e8f0' }}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Section 03 - 단가표 */}
              {sec.num === '03' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Preset buttons */}
                  <div>
                    <label style={S.inputLabel}>프리셋 추가</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {PRICE_TABLE_PRESETS.map(p => (
                        <button key={p} onClick={() => {
                          if (!formData.priceTable.find(r => r.item === p)) {
                            setFormData(prev => ({ ...prev, priceTable: [...prev.priceTable, { item: p, price: '', deliveryDays: '' }] }));
                          }
                        }} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 11, cursor: 'pointer', backgroundColor: formData.priceTable.find(r => r.item === p) ? '#f1f5f9' : '#fff', color: '#475569' }}>
                          + {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Price table */}
                  {formData.priceTable.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc' }}>
                          <th style={{ ...S.th2, width: '40%' }}>항목</th>
                          <th style={S.th2}>단가 (원)</th>
                          <th style={S.th2}>납기 (일)</th>
                          <th style={{ ...S.th2, width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.priceTable.map((row, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: 4 }}><input style={S.tableInput} value={row.item} onChange={e => updatePriceRow(i, 'item', e.target.value)} /></td>
                            <td style={{ padding: 4 }}><input style={S.tableInput} type="number" placeholder="50000" value={row.price} onChange={e => updatePriceRow(i, 'price', e.target.value)} /></td>
                            <td style={{ padding: 4 }}><input style={S.tableInput} type="number" placeholder="3" value={row.deliveryDays} onChange={e => updatePriceRow(i, 'deliveryDays', e.target.value)} /></td>
                            <td style={{ padding: 4 }}>
                              <button onClick={() => setFormData(prev => ({ ...prev, priceTable: prev.priceTable.filter((_, j) => j !== i) }))} style={{ ...S.iconBtn, width: 24, height: 24 }}><Trash2 size={12} color="#ef4444" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <button onClick={() => setFormData(prev => ({ ...prev, priceTable: [...prev.priceTable, { item: '', price: '', deliveryDays: '' }] }))} style={S.addRowBtn}>
                    <Plus size={14} /> 항목 추가
                  </button>

                  {/* Metrics */}
                  <div style={S.inputGrid}>
                    <div>
                      <label style={S.inputLabel}>리메이크율 (%)</label>
                      <input style={S.input} type="number" placeholder="2" value={formData.remakeRate} onChange={e => updateForm('remakeRate', e.target.value)} />
                    </div>
                    <div>
                      <label style={S.inputLabel}>월 처리량 (건/월)</label>
                      <input style={S.input} type="number" placeholder="150" value={formData.monthlyCapacity} onChange={e => updateForm('monthlyCapacity', e.target.value)} />
                    </div>
                    <div>
                      <label style={S.inputLabel}>첫 거래 혜택</label>
                      <input style={S.input} placeholder="예: 첫 3케이스 30% 할인" value={formData.firstDealBenefit} onChange={e => updateForm('firstDealBenefit', e.target.value)} />
                    </div>
                  </div>
                </div>
              )}

              {/* Section 04 - 포트폴리오 */}
              {sec.num === '04' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Portfolio images */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <label style={S.inputLabel}>작업 사진 ({portfolioPreviews.length}/20)</label>
                      <label style={S.uploadBtn}>
                        <Plus size={14} /> 추가
                        <input type="file" accept="image/*" multiple hidden onChange={handlePortfolioImagesChange} />
                      </label>
                    </div>
                    {portfolioPreviews.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 6 }}>
                        {portfolioPreviews.map((url, i) => (
                          <div key={i} style={{ position: 'relative' }}>
                            <img src={url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                            <button onClick={() => removePortfolioImage(i)} style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Studio images */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <label style={S.inputLabel}>기공소 내부 사진 ({studioPreviews.length}/10)</label>
                      <label style={S.uploadBtn}>
                        <Plus size={14} /> 추가
                        <input type="file" accept="image/*" multiple hidden onChange={handleStudioImagesChange} />
                      </label>
                    </div>
                    {studioPreviews.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 6 }}>
                        {studioPreviews.map((url, i) => (
                          <div key={i} style={{ position: 'relative' }}>
                            <img src={url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                            <button onClick={() => removeStudioImage(i)} style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Section 05 - 연락처 */}
              {sec.num === '05' && (
                <div style={S.inputGrid}>
                  <div>
                    <label style={S.inputLabel}>전화번호</label>
                    <input style={S.input} placeholder="010-0000-0000" value={formData.contactPhone} onChange={e => updateForm('contactPhone', e.target.value)} />
                  </div>
                  <div>
                    <label style={S.inputLabel}>이메일</label>
                    <input style={S.input} placeholder="lab@example.com" value={formData.contactEmail} onChange={e => updateForm('contactEmail', e.target.value)} />
                  </div>
                  <div>
                    <label style={S.inputLabel}>웹사이트</label>
                    <input style={S.input} placeholder="www.example.com" value={formData.contactWebsite} onChange={e => updateForm('contactWebsite', e.target.value)} />
                  </div>
                  <div>
                    <label style={S.inputLabel}>근무시간</label>
                    <input style={S.input} placeholder="평일 09:00~18:00" value={formData.workingHours} onChange={e => updateForm('workingHours', e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Bottom save button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20, gap: 8 }}>
          {adData && (
            <>
              <button onClick={() => toggleEditActive(true)} disabled={saving || adData.isActive === true}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', backgroundColor: adData.isActive === true ? '#f1f5f9' : '#10b981', color: adData.isActive === true ? '#94a3b8' : '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: adData.isActive === true ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                <ToggleRight size={16} /> 활성화
              </button>
              <button onClick={() => toggleEditActive(false)} disabled={saving || adData.isActive !== true}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', backgroundColor: adData.isActive !== true ? '#f1f5f9' : '#ef4444', color: adData.isActive !== true ? '#94a3b8' : '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: adData.isActive !== true ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                <ToggleLeft size={16} /> 비활성화
              </button>
            </>
          )}
          <button onClick={handleSave} disabled={saving} style={{ ...S.saveBtn, opacity: saving ? 0.6 : 1 }} title="내용만 저장 (활성/비활성 상태는 변경되지 않습니다)">
            <Save size={16} />
            {saving ? '저장 중...' : '내용 저장'}
          </button>
        </div>
      </div>
    );
  }

  // ===== LIST MODE =====
  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <BackfillSidoTool />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700 }}>기공소 홍보 관리</h1>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>전체 {labs.length}개 · 활성 {activeCount}개 · 비활성 {labs.length - activeCount}개</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => bulkToggle(true)} style={{ ...S.iconBtn, padding: '8px 14px', width: 'auto', height: 'auto', color: '#10b981', borderColor: '#a7f3d0', fontSize: 12, fontWeight: 600, gap: 4 }} title="필터 결과 일괄 활성화">
            <ToggleRight size={16} /> 일괄 활성
          </button>
          <button onClick={() => bulkToggle(false)} style={{ ...S.iconBtn, padding: '8px 14px', width: 'auto', height: 'auto', color: '#ef4444', borderColor: '#fecaca', fontSize: 12, fontWeight: 600, gap: 4 }} title="필터 결과 일괄 비활성화">
            <ToggleLeft size={16} /> 일괄 비활성
          </button>
          <button onClick={() => setShowLabPicker(true)} style={S.saveBtn}>
            <Plus size={16} /> 새 홍보페이지 작성
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, backgroundColor: '#fff' }}>
          <Search size={16} color="#94a3b8" />
          <input type="text" placeholder="기공소명, 주소, 슬로건 검색..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, backgroundColor: '#fff' }}>
          <option value="all">전체</option>
          <option value="active">활성</option>
          <option value="inactive">비활성</option>
        </select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <Building2 size={40} color="#d1d5db" />
          <p>등록된 홍보페이지가 없습니다</p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={S.th}>상태</th>
                <th style={S.th}>기공소명</th>
                <th style={S.th}>슬로건</th>
                <th style={S.th}>전문분야</th>
                <th style={S.th}>단가 (주요)</th>
                <th style={S.th}>스캐너</th>
                <th style={S.th}>리메이크</th>
                <th style={S.th}>수정자</th>
                <th style={S.th}>등록일</th>
                <th style={S.th}>관리</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lab, i) => {
                const specs = lab.specialties || [];
                const prices = Array.isArray(lab.priceTable) ? lab.priceTable.filter(p => p.price) : [];
                const scanners = Array.isArray(lab.compatibleScanners) ? lab.compatibleScanners : [];
                return (
                  <tr key={lab.id} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: i % 2 ? '#f8fafc' : '#fff', opacity: lab.isActive ? 1 : 0.65 }}>
                    <td style={S.td}>
                      <button
                        onClick={() => toggleActive(lab)}
                        title={lab.isActive ? '클릭하여 비활성화' : '클릭하여 활성화'}
                        style={{ padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700, border: '1px solid', cursor: 'pointer',
                          backgroundColor: lab.isActive ? '#f0fdf4' : '#f1f5f9',
                          color: lab.isActive ? '#10b981' : '#94a3b8',
                          borderColor: lab.isActive ? '#a7f3d0' : '#e2e8f0',
                          display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {lab.isActive ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                        {lab.isActive ? '활성' : '비활성'}
                      </button>
                    </td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{lab.businessName || '-'}</td>
                    <td style={{ ...S.td, color: '#6366f1', fontSize: 12 }}>{lab.slogan || '-'}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {specs.slice(0, 2).map((s, j) => <span key={j} style={{ padding: '2px 6px', backgroundColor: '#eef2ff', color: '#6366f1', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{s}</span>)}
                        {specs.length > 2 && <span style={{ fontSize: 10, color: '#94a3b8' }}>+{specs.length - 2}</span>}
                      </div>
                    </td>
                    <td style={{ ...S.td, fontSize: 11 }}>
                      {prices.length > 0 ? prices.slice(0, 2).map((p, j) => <div key={j}>{(p.item || '').replace(' 크라운', '')} {fmtPrice(p.price)}</div>) : '-'}
                    </td>
                    <td style={{ ...S.td, fontSize: 11 }}>{scanners.length > 0 ? `${scanners.length}종` : '-'}</td>
                    <td style={{ ...S.td, fontSize: 11, textAlign: 'center' }}>{lab.remakeRate ? `${lab.remakeRate}%` : '-'}</td>
                    <td style={{ ...S.td, fontSize: 11 }}>
                      <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        backgroundColor: lab.lastEditedBy === 'admin' ? '#fef3c7' : '#f1f5f9',
                        color: lab.lastEditedBy === 'admin' ? '#92400e' : '#64748b' }}>
                        {lab.lastEditedBy === 'admin' ? '관리자' : '기공소'}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontSize: 11, color: '#94a3b8' }}>{lab.createdAt?.toDate?.().toLocaleDateString() || '-'}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => startEdit(lab.id, lab.businessName)} style={{ ...S.iconBtn, color: '#6366f1', borderColor: '#c7d2fe' }} title="편집"><Edit3 size={14} /></button>
                        <button onClick={() => setSelectedLab(lab)} style={S.iconBtn} title="보기"><Eye size={14} /></button>
                        <button
                          onClick={() => toggleActive(lab)}
                          style={{ ...S.iconBtn, color: lab.isActive ? '#ef4444' : '#10b981', borderColor: lab.isActive ? '#fecaca' : '#a7f3d0' }}
                          title={lab.isActive ? '비활성화' : '활성화'}>
                          {lab.isActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Lab Picker Modal */}
      {showLabPicker && (
        <div style={S.overlay} onClick={() => setShowLabPicker(false)}>
          <div style={{ ...S.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>기공소 선택</h2>
              <button onClick={() => setShowLabPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>홍보페이지를 작성할 기공소를 선택하세요 (미작성 기공소 {labsWithoutAd.length}개)</p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, backgroundColor: '#fff', marginBottom: 12 }}>
              <Search size={16} color="#94a3b8" />
              <input type="text" placeholder="기공소명, 이메일 검색..." value={labPickerSearch} onChange={e => setLabPickerSearch(e.target.value)} style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }} />
            </div>

            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              {/* Already has ad - can also edit */}
              {labs.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>기존 홍보페이지 수정</p>
                  {labs.filter(l => {
                    if (!labPickerSearch.trim()) return true;
                    const q = labPickerSearch.toLowerCase();
                    return (l.businessName || '').toLowerCase().includes(q);
                  }).map(l => (
                    <div key={l.id} onClick={() => { setShowLabPicker(false); startEdit(l.id, l.businessName); }}
                      style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{l.businessName || '-'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{l.slogan || '슬로건 없음'}</div>
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, backgroundColor: l.isActive ? '#f0fdf4' : '#f1f5f9', color: l.isActive ? '#10b981' : '#94a3b8' }}>
                        {l.isActive ? '활성' : '비활성'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* No ad yet */}
              {labsWithoutAd.length > 0 && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>새로 작성</p>
                  {filteredLabPicker.map(u => (
                    <div key={u.id} onClick={() => startNewAd(u)}
                      style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{u.businessName || u.companyName || '이름 없음'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{u.email || ''} {u.address ? `· ${u.address}` : ''}</div>
                      </div>
                      <Plus size={16} color="#6366f1" />
                    </div>
                  ))}
                </div>
              )}

              {filteredLabPicker.length === 0 && labs.length === 0 && (
                <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>
                  <Users size={30} color="#d1d5db" />
                  <p>등록된 기공소가 없습니다</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal (read-only view) */}
      {selectedLab && (
        <div style={S.overlay} onClick={() => setSelectedLab(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{selectedLab.businessName || '기공소'}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setSelectedLab(null); startEdit(selectedLab.id, selectedLab.businessName); }} style={{ ...S.saveBtn, fontSize: 12, padding: '6px 12px' }}><Edit3 size={14} /> 편집</button>
                <button onClick={() => setSelectedLab(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
              </div>
            </div>

            {selectedLab.profileImageUrl && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <img src={selectedLab.profileImageUrl} alt="" style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' }} />
              </div>
            )}

            {selectedLab.slogan && <p style={{ margin: '0 0 12px', fontSize: 14, color: '#6366f1', fontWeight: 600, textAlign: 'center' }}>{selectedLab.slogan}</p>}

            <div style={S.infoCard}>
              <h4 style={S.infoTitle}>기본 정보</h4>
              <div style={S.infoGrid}>
                <div><span style={S.label}>대표자</span><span>{selectedLab.representative || '-'}</span></div>
                <div><span style={S.label}>설립</span><span>{selectedLab.foundingYear ? `${selectedLab.foundingYear}년` : '-'}</span></div>
                <div><span style={S.label}>리메이크율</span><span>{selectedLab.remakeRate ? `${selectedLab.remakeRate}%` : '-'}</span></div>
                <div><span style={S.label}>월 처리량</span><span>{selectedLab.monthlyCapacity ? `${selectedLab.monthlyCapacity}건` : '-'}</span></div>
                <div><span style={S.label}>첫 거래 혜택</span><span>{selectedLab.firstDealBenefit || '-'}</span></div>
                <div><span style={S.label}>수정자</span><span style={{ fontWeight: 600, color: selectedLab.lastEditedBy === 'admin' ? '#92400e' : '#64748b' }}>{selectedLab.lastEditedBy === 'admin' ? '관리자' : '기공소'}</span></div>
              </div>
            </div>

            {(selectedLab.introduction || selectedLab.detailedIntro) && (
              <div style={S.infoCard}>
                <h4 style={S.infoTitle}>소개</h4>
                <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{selectedLab.detailedIntro || selectedLab.introduction}</p>
              </div>
            )}

            {Array.isArray(selectedLab.priceTable) && selectedLab.priceTable.length > 0 && (
              <div style={S.infoCard}>
                <h4 style={S.infoTitle}>단가표</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr><th style={{ ...S.th2, width: '40%' }}>항목</th><th style={S.th2}>단가</th><th style={S.th2}>납기</th></tr></thead>
                  <tbody>
                    {selectedLab.priceTable.filter(p => p.price).map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 8px' }}>{p.item}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 600 }}>{fmtPrice(p.price)}</td>
                        <td style={{ padding: '6px 8px', color: '#64748b' }}>{p.deliveryDays ? `${p.deliveryDays}일` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selectedLab.specialties?.length > 0 && (
              <div style={S.infoCard}>
                <h4 style={S.infoTitle}>전문 분야</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedLab.specialties.map((s, i) => <span key={i} style={{ padding: '3px 10px', backgroundColor: '#eef2ff', color: '#6366f1', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{s}</span>)}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {Array.isArray(selectedLab.compatibleScanners) && selectedLab.compatibleScanners.length > 0 && (
                <div style={{ ...S.infoCard, flex: 1, minWidth: 200 }}>
                  <h4 style={S.infoTitle}>호환 스캐너</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedLab.compatibleScanners.map((s, i) => <span key={i} style={{ padding: '2px 8px', backgroundColor: '#f0fdf4', color: '#16a34a', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{s}</span>)}
                  </div>
                </div>
              )}
              {Array.isArray(selectedLab.deliveryRegions) && selectedLab.deliveryRegions.length > 0 && (
                <div style={{ ...S.infoCard, flex: 1, minWidth: 200 }}>
                  <h4 style={S.infoTitle}>배송 지역</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedLab.deliveryRegions.map((r, i) => <span key={i} style={{ padding: '2px 8px', backgroundColor: '#fff7ed', color: '#c2410c', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{r}</span>)}
                  </div>
                </div>
              )}
            </div>

            <div style={S.infoCard}>
              <h4 style={S.infoTitle}>연락처</h4>
              <div style={S.infoGrid}>
                <div><span style={S.label}>전화</span><span>{selectedLab.contactPhone || selectedLab.phone || '-'}</span></div>
                <div><span style={S.label}>이메일</span><span>{selectedLab.contactEmail || selectedLab.email || '-'}</span></div>
                <div><span style={S.label}>웹사이트</span><span>{selectedLab.contactWebsite || '-'}</span></div>
                <div><span style={S.label}>근무시간</span><span>{selectedLab.workingHours || '-'}</span></div>
                <div><span style={S.label}>주소</span><span>{selectedLab.address || '-'}</span></div>
              </div>
            </div>

            {Array.isArray(selectedLab.portfolioImages) && selectedLab.portfolioImages.length > 0 && (
              <div style={S.infoCard}>
                <h4 style={S.infoTitle}>포트폴리오 ({selectedLab.portfolioImages.length}장)</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 6 }}>
                  {selectedLab.portfolioImages.map((url, i) => (
                    <img key={i} src={url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(selectedLab.studioImages) && selectedLab.studioImages.length > 0 && (
              <div style={S.infoCard}>
                <h4 style={S.infoTitle}>기공소 내부 ({selectedLab.studioImages.length}장)</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 6 }}>
                  {selectedLab.studioImages.map((url, i) => (
                    <img key={i} src={url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  th: { padding: '8px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' },
  td: { padding: '8px 8px', fontSize: 13, color: '#334155' },
  th2: { padding: '6px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #e2e8f0' },
  iconBtn: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: 6, backgroundColor: '#fff', color: '#64748b', cursor: 'pointer' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { backgroundColor: '#fff', borderRadius: 16, padding: 24, maxWidth: 700, width: '100%', maxHeight: '90vh', overflow: 'auto' },
  infoCard: { padding: 14, backgroundColor: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', marginBottom: 10 },
  infoTitle: { margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#0f172a' },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  label: { display: 'block', fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 2 },
  section: { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16 },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' },
  sectionNum: { width: 28, height: 28, borderRadius: '50%', backgroundColor: '#eef2ff', color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 },
  input: { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  inputLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 },
  inputGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 },
  tableInput: { width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, outline: 'none', boxSizing: 'border-box' },
  saveBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  uploadBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 14px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  addRowBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px dashed #d1d5db', borderRadius: 8, backgroundColor: '#fff', color: '#64748b', fontSize: 13, cursor: 'pointer', justifyContent: 'center' },
  successBanner: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', backgroundColor: '#f0fdf4', color: '#16a34a', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600 },
  errorBanner: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600 },
};

// 위치 기반 분할용 — labAdvertisements에 sido 필드 일괄 채우기
function BackfillSidoTool() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm('모든 기공소 홍보 문서에 sido(시도) 필드를 자동 추가합니다. 진행할까요?\n\n주소에서 시도를 추출해서 위치 기반 검색에 사용됩니다. 1회만 실행하면 됩니다.')) return;
    setBusy(true);
    setResult(null);
    try {
      const fns = getFunctions(undefined, 'asia-northeast3');
      const fn = httpsCallable(fns, 'backfillLabSido');
      const res = await fn({});
      const d = res.data;
      setResult(`✅ 전체 ${d.total}개 · 업데이트 ${d.updated}개 · 스킵 ${d.skipped}개 (이미 있거나 주소 없음)`);
    } catch (e) {
      setResult(`❌ ${e?.message || '실패'}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 16 }}>
      <h3 style={{ fontSize: 13, fontWeight: 800, margin: '0 0 6px 0' }}>📍 위치 기반 분할 (sido 백필)</h3>
      <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 10px 0' }}>
        기존 홍보 데이터에 시도(sido) 필드를 추가합니다. 추가 후 사용자 위치 기반으로 빠르게 검색됩니다.
      </p>
      <button onClick={run} disabled={busy}
        style={{ padding: '8px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
        {busy ? '실행 중...' : '🔄 sido 백필 실행 (1회)'}
      </button>
      {result && <div style={{ marginTop: 8, fontSize: 12, color: result.startsWith('✅') ? '#065f46' : '#991b1b' }}>{result}</div>}
    </div>
  );
}

export default AdminLabAdvertising;
